import { createContext, useContext } from 'react'
import styles from './Tabs.module.css'

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface TabsContextValue {
  value: string | null
  onChange: ((value: string) => void) | null
}

const TabsContext = createContext<TabsContextValue>({ value: null, onChange: null })

function useTabsContext() {
  return useContext(TabsContext)
}

// ---------------------------------------------------------------------------
// Tabs (root wrapper)
// ---------------------------------------------------------------------------

export interface TabsProps {
  /** Currently active tab value (controlled) */
  value?: string
  /** Called when a tab is clicked */
  onChange?: (value: string) => void
  children: React.ReactNode
  className?: string
}

export function Tabs({ value, onChange, children, className }: TabsProps) {
  return (
    <TabsContext.Provider value={{ value: value ?? null, onChange: onChange ?? null }}>
      <div className={className}>
        {children}
      </div>
    </TabsContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// TabList (the row of tab buttons)
// ---------------------------------------------------------------------------

export interface TabListProps {
  children: React.ReactNode
  className?: string
}

export function TabList({ children, className }: TabListProps) {
  const classes = [styles.tabList, className].filter(Boolean).join(' ')
  return (
    <div className={classes} role="tablist">
      {children}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab (individual tab button)
// ---------------------------------------------------------------------------

export interface TabProps {
  /** Value that identifies this tab */
  value: string
  children: React.ReactNode
  /**
   * A count (or short text) after the label, drawn in the tab's own pill. Pass
   * the value itself, never a <Badge>: the tab already styles it, and a Badge
   * inside nests a pill in a pill. A count of 0 still renders; null or false
   * renders nothing.
   */
  badge?: number | string | null | false
  className?: string
}

export function Tab({ value, children, badge, className }: TabProps) {
  const { value: activeValue, onChange } = useTabsContext()
  const isActive = activeValue === value
  const classes = [
    styles.tab,
    isActive ? styles.tabActive : '',
    className,
  ].filter(Boolean).join(' ')

  return (
    <button
      className={classes}
      role="tab"
      aria-selected={isActive}
      onClick={() => onChange?.(value)}
      type="button"
    >
      {children}
      {/* Not `badge &&`: a count of 0 is falsy, and React prints it as a bare
          "0" glued to the label with no badge styling. */}
      {badge != null && badge !== false && badge !== '' && (
        <span className={styles.tabBadge}>{badge}</span>
      )}
    </button>
  )
}

// ---------------------------------------------------------------------------
// TabPanel (content that renders when its tab is active)
// ---------------------------------------------------------------------------

export interface TabPanelProps {
  /** Must match a Tab's value */
  value: string
  children: React.ReactNode
  className?: string
}

export function TabPanel({ value, children, className }: TabPanelProps) {
  const { value: activeValue } = useTabsContext()
  if (activeValue !== value) return null

  return (
    <div className={className} role="tabpanel">
      {children}
    </div>
  )
}
